import { ReactNode } from "react";
import { Card, Text, Title, Select, SelectItem } from "@tremor/react";
import type { RestaurantIdentity } from "./DashboardHeader";

// ── Sidebar compartida de los 3 tableros ───────────────────
// Plantilla base: la de Operaciones (w-36, Restaurante + Mes).
// Los tableros que necesiten filtros extra (Comercial → Canal) los pasan
// como `children` (se renderizan debajo de Mes, antes del cierre del card).

export interface SidebarMonth {
  anio: number;
  mm: number;
  label: string;
}

export default function DashboardSidebar({
  restaurants,
  selectedRestaurantId,
  onRestaurantChange,
  months,
  selectedMonth,
  onMonthChange,
  sidebarOpen,
  onToggleSidebar,
  children,
}: {
  restaurants: RestaurantIdentity[];
  selectedRestaurantId: string;
  onRestaurantChange: (id: string, slug: string) => void;
  months: SidebarMonth[];
  selectedMonth: { anio: number; mm: number } | null;
  onMonthChange: (m: { anio: number; mm: number }) => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  children?: ReactNode;
}) {
  return (
    <aside
      className={`shrink-0 transition-all duration-200 ${
        sidebarOpen ? "w-36" : "w-10"
      }`}
    >
      <Card className="!p-3 sticky top-20">
        <div className="flex items-center justify-between mb-3">
          {sidebarOpen && <Title className="!text-sm">Filtros</Title>}
          <button
            onClick={onToggleSidebar}
            className="p-1 rounded hover:bg-tremor-background-subtle text-tremor-content"
            title={sidebarOpen ? "Ocultar filtros" : "Mostrar filtros"}
            aria-label="Toggle filtros"
          >
            {sidebarOpen ? "◀" : "▶"}
          </button>
        </div>

        {sidebarOpen && (
          <div className="space-y-3">
            <div>
              <Text className="text-xs mb-1">Restaurante</Text>
              <Select
                value={selectedRestaurantId}
                onValueChange={(v) => {
                  const r = restaurants.find((x) => x.id === v);
                  onRestaurantChange(v, r?.slug ?? "");
                }}
                className="w-full !min-w-0 !py-1 !text-xs"
              >
                {restaurants.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div>
              <Text className="text-xs mb-1">Mes</Text>
              <Select
                value={selectedMonth ? `${selectedMonth.anio}-${selectedMonth.mm}` : ""}
                onValueChange={(v) => {
                  const [anio, mm] = v.split("-").map(Number);
                  onMonthChange({ anio, mm });
                }}
                className="w-full !min-w-0 !py-1 !text-xs"
              >
                {months.map((m) => (
                  <SelectItem key={`${m.anio}-${m.mm}`} value={`${m.anio}-${m.mm}`}>
                    {m.label}
                  </SelectItem>
                ))}
              </Select>
            </div>

            {children}
          </div>
        )}
      </Card>
    </aside>
  );
}
